import { collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { FileData } from '../types';

interface OfferDocuments {
  offerId: string;
  files?: FileData[];
  purchaseAgreementFiles?: FileData[];
  rawText?: string;
  createdAt?: any;
  updatedAt?: any;
}

export const uploadFile = async (file: File, userId: string): Promise<FileData> => {
  // Create unique file path
  const timestamp = Date.now();
  const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const filePath = `offers/${userId}/${timestamp}_${sanitizedFileName}`;
  
  // Upload to Firebase Storage with explicit content type
  const storageRef = ref(storage, filePath);
  await uploadBytes(storageRef, file, { contentType: 'application/pdf' });
  
  // Get download URL - this is what should be stored for downloading
  const downloadURL = await getDownloadURL(storageRef);
  
  return {
    filePath,
    downloadURL,  // Store the actual download URL
    fileName: file.name,
    fileUploadedDate: new Date(),
  };
};

export const createOffer = async (
  userId: string,
  files: FileData[],
  listingId?: string,
  manualAddress?: string
): Promise<string> => {
  // Create offer with all required fields that processOfferPDFs expects
  const offerData: any = {
    userId,
    submissionDate: serverTimestamp(),
    files,
    purchaseAgreementFiles: [], // Will be populated by Cloud Function
    parsingStatus: 'pending',
    processingCompleted: false,
    offerScore: 'pending', // Will be calculated by Cloud Function
    submittedVia: 'web-form',
    status: 'pending',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  // Add listingId if provided
  if (listingId) {
    offerData.listingId = listingId;
  }

  // Add manual property address if provided (used for auto-listing matching)
  if (manualAddress?.trim()) {
    offerData.manualPropertyAddress = manualAddress.trim();
  }
  
  const docRef = await addDoc(collection(db, 'offers'), offerData);
  
  return docRef.id;
};

/**
 * Fetch offer documents from the offer-documents collection
 * This contains heavy file metadata separated from the lightweight offers collection
 */
export const fetchOfferDocuments = async (offerId: string): Promise<OfferDocuments | null> => {
  try {
    const docRef = doc(db, 'offer-documents', offerId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        offerId,
        ...docSnap.data()
      } as OfferDocuments;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching offer documents:', error);
    return null;
  }
};
